import { type LinkType, type DatasourceDataResponse } from '@atlaskit/linking-types';
import { AnyAri } from '@atlassian/ari/any-ari';
import { type DatabaseField, DatabaseFieldType } from '@databases/types/database';
import { generateId } from '@databases/utils/utils';
import { uniq } from 'lodash-es';

import { type DatasourceEntryValue, type IDatasourceAdapter } from '../DatasourceAdapter';
import { type DatasourceInput } from '../DatasourceInput';
import {
  getDatasourceResponseByType,
  requiredConfluenceDatasourceColumns
} from '../content/datasource';
import { type CreateFieldParams } from '../types';

export const transformConfluenceResult = (input: DatasourceDataResponse) => {
  const fieldsNeedingUnencode = ['description'];

  const result = {
    ...input,
    data: {
      ...input.data,
      items: input.data.items.map((item) => {
        const newItem = { ...item };
        fieldsNeedingUnencode.forEach((field) => {
          if (newItem[field]) {
            // decode the html entities
            const nsHtml =
              '<html xmlns="http://www.w3.org/1999/xhtml" xmlns:ac="http://example.com/ac" xmlns:ri="http://example.com/ri" xmlns:fab="http://example.com/fab"><body>' +
              newItem[field].data +
              '</body></html>';
            const parser = new window.DOMParser();
            const tree = parser.parseFromString(nsHtml, 'text/html');
            newItem[field] = {
              data: tree.body.textContent || ''
            };
          }
        });
        return newItem;
      })
    }
  };
  return result;
};

class ConfluenceDatasource implements IDatasourceAdapter {
  constructor(private input: DatasourceInput) {}

  public getPrimaryFieldSettings() {
    const cloudId = this.input.getCloudId();
    return cloudId ? { cloudId } : {};
  }

  public getDependentFieldSettings(
    field: DatabaseField<DatabaseFieldType.PAGE_DETAILS>,
    column: string
  ) {
    return {
      fieldId: field.id,
      contentField: column
    };
  }

  public async loadDatasourceValues(
    _: CreateFieldParams,
    pageSize: number,
    pageCursor: string
  ): Promise<{
    values: DatasourceEntryValue[];
    nextPageCursor: string | undefined;
    totalCount: number | undefined;
  }> {
    const { items, nextPageCursor, totalCount } = await this.getDatasourceResponseItems(
      this.input.getUniqueColumns(),
      pageSize,
      pageCursor
    );

    const entryValues: DatasourceEntryValue[] = items.map((item) => {
      const { id, space, title } = item;
      return {
        type: DatabaseFieldType.PAGE_LINK,
        value: [
          {
            type: 'page',
            contentId: AnyAri.parse(id.data as string).resourceId,
            ari: id.data as string,
            contentTitle: (title.data as LinkType['value']).text as string,
            spaceKey: (space.data as LinkType['value']).url.split('/').at(-1) as string,
            _id: generateId()
          }
        ]
      };
    });

    return {
      values: entryValues,
      nextPageCursor,
      totalCount
    };
  }

  private async getDatasourceResponseItems(
    columns: string[],
    pageSize: number,
    pageCursor: string
  ) {
    const fields = uniq([...requiredConfluenceDatasourceColumns, ...columns]);

    const response = await getDatasourceResponseByType(
      'confluence',
      fields,
      this.input.getParameters(),
      pageSize,
      pageCursor
    );

    if (!response || !response.data) {
      return { items: [] };
    }

    return transformConfluenceResult(response).data;
  }
}

export default ConfluenceDatasource;
